import {
  DISCIPLINE_MEMBERSHIP_FIELDS,
  getHorseNumberFieldName,
  getUserFieldName,
  NumberMembershipFieldKey,
} from '../../../../../../../const/discipline-memberships-const'
import { scoreStatusOptions } from '../../../../../../../const/score-card-const'
import { formatDate } from '../../../../../../../helpers/helpers'
import { IHorseData } from '../../../../../../../models/horse/horse.interface'
import {
  IJudge,
  IRegistrationByDayInterface,
} from '../../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IRiderTeamMemberInterface } from '../../../../../../../models/rider-team-member/riderTeamMember.interface'
import {
  ISelect,
  IUserDocuments,
  IUserInterface,
} from '../../../../../../../models/users/user.interface'

export const hasBeenScored = (entry: IRegistrationByDayInterface) => {
  return !!entry.score?.totalPoints || entry.score?.totalPoints === 0
}

/**
 * Helper function to calculate event statistics from registered users by day
 * @param registeredUsersByDay - Array of registered users by day entries
 * @returns Object with various statistics and status counts
 */
export const calculateEventStats = (
  registeredUsersByDay: IRegistrationByDayInterface[] = []
): {
  numberOfEntries: number
  numberOfScratches: number
  numberOfCompeted: number
  numberOfCompleted: number
  runStatusCounts: { [key: string]: number }
} => {
  if (!registeredUsersByDay || !Array.isArray(registeredUsersByDay)) {
    return {
      numberOfEntries: 0,
      numberOfScratches: 0,
      numberOfCompeted: 0,
      numberOfCompleted: 0,
      runStatusCounts: {},
    }
  }

  const scoreStatuses = scoreStatusOptions.map((option) => option.value)

  const runStatusCounts: Record<string, number> = {}
  scoreStatuses.forEach((status) => {
    runStatusCounts[status] = 0
  })

  // total entries is len of the arr
  const numberOfEntries = registeredUsersByDay.length

  // count scratched entries
  const numberOfScratches = registeredUsersByDay.filter(
    (entry) => entry.isScratched === true
  ).length

  // count entries that competed (not scratched and have totalPoints)
  const numberOfCompeted = registeredUsersByDay.filter((entry) => {
    const notScratched = entry.isScratched !== true
    return notScratched && hasBeenScored(entry)
  }).length

  // count entries that completed (competed and don't have a disqualifying score status)
  let numberOfCompleted = 0

  registeredUsersByDay.forEach((entry) => {
    // skip entries that are scratched or don't have score
    if (entry.isScratched === true || !entry.score || !hasBeenScored(entry)) return

    // if entry has judges with scoreStatus, count them
    if (entry.score.judges && Array.isArray(entry.score.judges)) {
      let entryHasDisqualifyingStatus = false

      if (entry.score.scoreStatus) {
        // increment the count for this status
        runStatusCounts[entry.score.scoreStatus] = (runStatusCounts[entry.score.scoreStatus] ?? 0) + 1

        // mark this entry has a disqualifying status
        entryHasDisqualifyingStatus = true
      }

      // if entry doesn't have any disqualifying status
      if (!entryHasDisqualifyingStatus) {
        numberOfCompleted++
      }
    } else {
      // no judges or no scoreStatus but a score - counts as completed
      numberOfCompleted++
    }
  })

  return {
    numberOfEntries,
    numberOfScratches,
    numberOfCompeted,
    numberOfCompleted,
    runStatusCounts,
  }
}

// Helper function to check if a certificate is expired
export const checkCertificateStatus = (
  inspectionDate: string | Date | null | undefined,
  expirationMonths: number
): string => {
  if (!inspectionDate) {
    return ''
  }

  try {
    const inspectionDateObj = new Date(inspectionDate)
    const currentDate = new Date()

    // calc the expiration date by adding the specified months param
    const expirationDate = new Date(inspectionDateObj)
    expirationDate.setMonth(expirationDate.getMonth() + expirationMonths)

    // check if current date is past the expiration date
    if (currentDate > expirationDate) {
      return `Expired, Last Approved: ${formatDate(inspectionDate)}`
    } else {
      return `Valid, Approved: ${formatDate(inspectionDate)}`
    }
  } catch (error) {
    console.error('Error parsing inspection date:', error)
    return ''
  }
}

// Helper function to format horse year of birth
export const formatHorseYearOfBirth = (
  dob?: Date | { __time__: string } | string | null
): string => {
  if (!dob) return ''

  try {
    // if an object with __time__ property
    if (typeof dob === 'object' && '__time__' in dob) {
      const date = new Date(dob.__time__)
      return date.getFullYear().toString()
    }

    // if  already a Date object
    if (dob instanceof Date) {
      return dob.getFullYear().toString()
    }

    const date = new Date(dob)

    if (isNaN(date.getTime())) {
      return ''
    }

    return date.getFullYear().toString()
  } catch {
    return ''
  }
}

// Helper func to convert 24-hour time format (HH:MM:SS) to 12-hour format with AM/PM
export const convertTo12HourReportFormat = (time24: string | null | undefined): string => {
  try {
    if (!time24 || time24 === 'Not set') {
      return 'Not set'
    }

    // split time string - always expects format "HH:MM:SS"
    const timeParts = time24.split(':')
    if (timeParts.length !== 3) {
      return 'Not set'
    }

    const hours = parseInt(timeParts[0], 10)
    const minutes = timeParts[1]
    const seconds = timeParts[2]

    if (isNaN(hours) || hours < 0 || hours > 23) {
      return 'Not set'
    }

    // convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours

    // only show seconds if not "00"
    if (seconds !== '00') {
      return `${displayHours}:${minutes}:${seconds} ${period}`
    } else {
      return `${displayHours}:${minutes} ${period}`
    }
  } catch (err) {
    console.error('Error converting time format:', err)
    return 'Not set'
  }
}

// Helper function to get all membership numbers for a horse
export const getHorseMembershipNumbers = (horse: IHorseData): string => {
  // get non-empty membership numbers
  const membershipNumbers = DISCIPLINE_MEMBERSHIP_FIELDS.map((field) => {
    const fieldName = getHorseNumberFieldName(field.key)
    const number = horse[fieldName as keyof IHorseData]
    return number && String(number).trim() !== '' ? `${field.key}: ${number}` : null
  }).filter(Boolean)
  return membershipNumbers.join(', ')
}

export const getFormattedUserName = (user: IUserInterface | undefined): string => {
  if (!user) return ''

  if (user.userFullName) {
    return user.userFullName
  }

  if (user.userFirstName && user.userLastName) {
    return `${user.userFirstName} ${user.userLastName}`
  }

  return user.userName || ''
}

// Helper function to check if a date is the lifetime membership date (Dec 31, 2099)
const isLifetimeMembershipDate = (dateStr: string | Date | null): boolean => {
  if (!dateStr) return false

  try {
    const date = new Date(dateStr)
    return date.getFullYear() === 2099 && date.getMonth() === 11 && date.getDate() === 31
  } catch (e) {
    return false
  }
}

// Helper function to get user discipline numbers with optional expiration
export const getUserDisciplineNumbers = (user: IUserInterface): string => {
  // Generate discipline fields from the common constants
  const disciplineFields: Array<{
    key: string
    numberField: NumberMembershipFieldKey
  }> = DISCIPLINE_MEMBERSHIP_FIELDS.map((field) => ({
    key: field.key,
    numberField: getUserFieldName(field.key) as NumberMembershipFieldKey,
  }))

  // get non-empty discipline numbers with optional expiration
  const disciplineNumbers = disciplineFields
    .map((field) => {
      const membershipObj = user[field.numberField]
      if (!membershipObj) return null

      // Check if membershipObj is an obj with documentNumber property - IUserDocuments type
      if (typeof membershipObj === 'object' && membershipObj !== null) {
        const typedMembershipObj = membershipObj as IUserDocuments
        // Extract the membership number and date from the object
        const number = typedMembershipObj.documentNumber
        const endDate = typedMembershipObj.endDate
        const membershipStatus = typedMembershipObj.membershipStatus

        if (!number) return null

        // Check if this is a lifetime membership based on the date or status
        const isLifetime =
          isLifetimeMembershipDate(endDate) ||
          (membershipStatus && membershipStatus.toLowerCase() === 'lifetime')

        // format w/ appropriate expiration info
        let formattedNumber
        if (isLifetime) {
          formattedNumber = `${field.key}: ${number} (Lifetime membership)`
        } else if (endDate) {
          formattedNumber = `${field.key}: ${number} (Exp: ${formatDate(new Date(endDate))})`
        } else {
          formattedNumber = `${field.key}: ${number}`
        }
        return formattedNumber
      }
      return null
    })
    .filter(Boolean) as string[]

  return disciplineNumbers.join(', ')
}

// Handle the case where userFirstName or userLastName might be empty
// but userFullName exists - split the full name into first and last components
export const getFirstAndLastNames = (
  user: IUserInterface
): { firstName: string; lastName: string } => {
  // use first & last if we already have both first and last name
  if (user.userFirstName && user.userLastName) {
    return {
      firstName: user.userFirstName,
      lastName: user.userLastName,
    }
  }

  // if have a full name but missing first or last name, split the full name
  if (user.userFullName) {
    const nameParts = user.userFullName.trim().split(/\s+/)

    // if  only one part, use it as the first name and leave last name empty
    if (nameParts.length === 1) {
      return {
        firstName: nameParts[0],
        lastName: '',
      }
    }

    // or use the first part as the first name and the rest as the last name
    const firstName = nameParts[0]
    const lastName = nameParts.slice(1).join(' ')

    return {
      firstName,
      lastName,
    }
  }

  return {
    firstName: user.userFirstName || '',
    lastName: user.userLastName || '',
  }
}

/**
 * Gets a comma-separated list of team member names for a rider in a specific role
 *
 * @param userId The ID of the rider
 * @param role The role to get team members for (e.g., 'Trainer', 'Owner', 'Coach', 'Guardian')
 * @param teamMembersMap The map of team members by rider ID and role
 * @returns A comma-separated string of team member names, or empty string if none found
 */
export const getTeamMemberNamesByRole = (
  userId: string | null,
  role: string,
  teamMembersMap: Map<string, Map<string, IRiderTeamMemberInterface[]>>
): string => {
  if (!userId) return ''

  const roleMap = teamMembersMap.get(userId)
  if (!roleMap) return ''

  const members = roleMap.get(role) || []
  return members.map((member) => member.teamMemberName || '').join(', ')
}

// Utility function to calculate judge percentage and score
export const calculateJudgeMetrics = (judge: IJudge): { percentage: number; score: number } => {
  let totalPoints = 0
  let maxPoints = 0

  judge.sections?.forEach((section: any) => {
    section.rows?.forEach((row: any) => {
      // skip empty or unexpected format
      if (!row.score || isNaN(Number(row.score))) return

      if (row.coefficient) {
        // if there is a coefficient - multiply score by coefficient
        totalPoints += Number(row.coefficient) * Number(row.score)
      } else {
        totalPoints += Number(row.score)
      }
    })

    // add section's max points to total max
    maxPoints += section.max || 0
  })

  // subtract any errors
  totalPoints -= judge.errors || 0

  // calc percentage
  const percentage = maxPoints ? (totalPoints / maxPoints) * 100 : 0

  return {
    percentage: Number(percentage.toFixed(3)),
    score: Number(totalPoints.toFixed(2)),
  }
}

// utility function to get a string value from a potential ISelect object as some properties are typed with a union type
export const getStringValue = (value: string | ISelect | null | undefined): string => {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && 'value' in value) {
    return value.value || ''
  }
  return ''
}
